import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { Container, Col, Row } from 'reactstrap';
import Breadcrumbs from '../../../layout/Breadcrumbs';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import ParcelForm from './ParcelForm';
import { postParcel } from '../../../../actions';
import { CARGO_PATH } from '../../../../config/paths';
import { tzNormalizeDate } from '../../../../utils/date';
import {
  buildTransactionList,
  flagUsedPinpadTransactions,
  summarizeSplit,
} from '../../../../utils/units/paymentSplit';

export const NewParcel = ({ breadcrumbs, dispatchPostParcel }) => {
  const onSubmit = (
    formValues,
    hasDelivery,
    isDiscountCodeChecked,
    listPrice,
    remainingPrice,
    netDiscountAmount,
    discountCodeSubCategories,
    yangoPrice,
  ) => {
    const {
      voucherType,
      sourceLocation,
      destinationLocation,
      senderCustomer,
      consigneeCustomer,
      note,
      cargaItems,
      customer,
      business,
      corporateCreditAmount,
      salePrice,
      uuid,
    } = formValues;

    // validate split
    const summary = summarizeSplit(
      formValues.transactionList || [],
      remainingPrice,
    );

    if (summary.hasDuplicateMethods) {
      toastr.error(
        'Error',
        'No puede repetir el mismo método de pago en dos filas',
      );
      return;
    }

    if (!summary.isValid) {
      toastr.error(
        'Error',
        summary.isPending
          ? 'Los montos no cubren el total a pagar'
          : 'Los montos superan el total a pagar',
      );
      return;
    }

    // build transactions
    const { transactions, freeOfCharge } = buildTransactionList(
      formValues.transactionList || [],
      {
        totalPrice: remainingPrice,
        customerId: customer ? customer.value : senderCustomer.value,
        voucherTypeId: voucherType.value,
        businessId: business ? business.value : null,
      },
    );

    // normalize dates for display in summary
    transactions.forEach((t) => {
      if (t.depositDate) {
        // eslint-disable-next-line no-param-reassign
        t.depositDate = tzNormalizeDate({ date: t.depositDate });
      }
    });

    const newFormValues = {
      ...formValues,
      voucherTypeId: voucherType.value,
      sourceCityId: sourceLocation.city.id,
      sourceLocationId: sourceLocation.value,
      destinationCityId: destinationLocation.city.id,
      destinationLocationId: destinationLocation.value,
      senderCustomerId: senderCustomer.value,
      consigneeCustomerId: consigneeCustomer.value,
      paymentMethodId:
        transactions.length > 0 ? transactions[0].paymentMethodId : null,
      comments: note,
      cargoItemList: cargaItems.map((item) => ({
        cargoCategoryId: item.cargoCategoryId
          ? item.cargoCategoryId.value
          : null,
        commonProductId: item.commonProductId
          ? item.commonProductId.value
          : null,
        description: item.description,
        listPrice: item.listPrice,
        salePrice: item.salePrice,
        quantity: item.quantity,
        weight: item.weight,
        length: item.length,
        width: item.width,
        height: item.height,
        perishable: !!item.perishable,
      })),
      listPrice,
      salePrice,
      customerId: customer ? customer.value : null,
      businessId: business ? business.value : null,
      discountCodeString: isDiscountCodeChecked
        ? formValues.discountCode
        : null,
      salesPriceWithDiscount:
        isDiscountCodeChecked || corporateCreditAmount
          ? parseFloat(formValues.discountPrice)
          : null,
      netDiscountAmount: netDiscountAmount || null,
      contingencyMotive: formValues.contingencyMotive
        ? formValues.contingencyMotive.value
        : null,
      attachedDocumentList: formValues.attachedDocuments
        ? formValues.attachedDocuments.map((document) => ({
            attachedDocumentCorrelation: document.attachedDocument,
          }))
        : [],
      transactionList: transactions,
      corporateCreditAmount: corporateCreditAmount || null,
      contingencyAssignmentDocumentDate:
        formValues.contingencyAssignmentDocumentDate
          ? tzNormalizeDate({
              date: formValues.contingencyAssignmentDocumentDate,
            })
          : null,
      pinPadTransactions: flagUsedPinpadTransactions(
        formValues.pinPadTransactions || [],
        formValues.transactionList || [],
      ),
    };
    if (formValues.secondaryConsignee) {
      newFormValues.secondaryConsigneeCustomerId =
        formValues.secondaryConsigneeCustomer.value;
    }

    let discountCodeSubCategoryId = null;
    if (
      Array.isArray(discountCodeSubCategories) &&
      discountCodeSubCategories.length === 1
    ) {
      discountCodeSubCategoryId = discountCodeSubCategories[0].value;
    } else if (formValues.discountCodeSubCategory) {
      discountCodeSubCategoryId = formValues.discountCodeSubCategory.value;
    }
    if (discountCodeSubCategoryId !== null) {
      newFormValues.discountCodeSubCategoryId = discountCodeSubCategoryId;
    }

    let discountCodeAuthorizerId = null;
    if (formValues.discountCodeSubCategoryAuthorizer) {
      discountCodeAuthorizerId =
        formValues.discountCodeSubCategoryAuthorizer.value;
    }
    if (discountCodeAuthorizerId !== null) {
      newFormValues.discountCodeAuthorizerId = discountCodeAuthorizerId;
    }

    if (hasDelivery) {
      const loc = formValues.deliveryAddress;
      if (!loc || !loc.lat || !loc.lng) {
        toastr.error(
          'Error',
          'Por favor Digite y Seleccione una dirección de entrega válida',
        );
        return;
      }

      if (formValues.secondaryConsignee) {
        newFormValues.secondaryConsigneeCustomerId =
          formValues.secondaryConsigneeCustomer.value;
      }

      newFormValues.deliveryDistrictId = formValues.district.value;
      newFormValues.deliveryAddress = formValues.deliveryAddress.address;
      newFormValues.deliveryLatitude = formValues.deliveryAddress.lat;
      newFormValues.deliveryLongitude = formValues.deliveryAddress.lng;
      newFormValues.deliveryPostalCode = formValues.deliveryAddress.postalCode;
    }

    if (formValues.increasedPrice) {
      newFormValues.increasedPrice = formValues.increasedPrice.value;
    }

    newFormValues.uuid = uuid;
    newFormValues.yangoDelivery = yangoPrice;

    // free of charge
    if (freeOfCharge.categoryId !== null) {
      newFormValues.freeOfChargeCategoryId = freeOfCharge.categoryId;
    }
    if (freeOfCharge.subCategoryId !== null) {
      newFormValues.freeOfChargeSubCategoryId = freeOfCharge.subCategoryId;
    }
    if (freeOfCharge.userId !== null) {
      newFormValues.freeOfChargeUserId = freeOfCharge.userId;
    }

    dispatchPostParcel(newFormValues);
  };

  return (
    <Container fluid>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Row>
        <Col>
          <h1>Recepción de Carga</h1>
          <p>Crear nueva Recepción de Carga.</p>
        </Col>
      </Row>
      <ParcelForm onSubmit={onSubmit} />
    </Container>
  );
};

const mapStateToProps = ({ CargoUnit, SalesUnit }) => ({
  breadcrumbs: [
    ...CargoUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Carga',
      href: CARGO_PATH,
    },
    {
      text: 'Recepción de Carga',
      href: '',
    },
  ],
  activeSaleSession: SalesUnit.SalesSession.getIn(['active', 'content']),
});

const mapDispatchToProps = { dispatchPostParcel: postParcel };

NewParcel.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchPostParcel: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewParcel);
